/**
 * Supported UI languages and the helpers that keep the active language,
 * the stored choice (localStorage `i18nextLng`) and <html lang/dir> in sync.
 *
 * Every switcher (app header, landing page, module detail page) reads this
 * list through components/LanguageSwitcher. Codes are always the BASE
 * language ("en", never "en-US"): browsers report regional variants, and
 * before this module the header switcher compared exact codes, so an en-US
 * visitor saw English text with a "TR" switcher and <html lang="tr">.
 *
 * Pure module: it never imports ../i18n, so components and tests can use it
 * with a mocked i18n instance.
 */
import inProgressConfig from './languagesInProgress.json';

export const LANGUAGE_STORAGE_KEY = 'i18nextLng';
export const DEFAULT_LANGUAGE = 'tr';

// Languages whose translation is still being filled in (see the JSON file and
// src/locales/README.md). They are selectable; the switcher marks them "beta".
export const IN_PROGRESS_LANGUAGES = inProgressConfig.inProgress;

// Switcher groups, in display order. `labelKey` is translated in the UI.
export const LANGUAGE_GROUPS = [
  { id: 'turkic', labelKey: 'shell.languageSwitcher.groups.turkic' },
  { id: 'europe', labelKey: 'shell.languageSwitcher.groups.europe' },
  { id: 'middleEastAfrica', labelKey: 'shell.languageSwitcher.groups.middleEastAfrica' },
  { id: 'asia', labelKey: 'shell.languageSwitcher.groups.asia' },
];

// Intl tags (see utils/format.js):
// - `intl`         plain BCP-47 tag: speech recognition / synthesis,
//                  og:locale, and numbers + money unless `numberIntl` is set.
// - `numberIntl`   tag for numbers and money when it differs from `intl`.
// - `dateIntl`     tag for dates and times when it differs. Owner decisions:
//                  dates are Gregorian everywhere (fa: fa-IR-u-ca-gregory,
//                  ar: ar-u-ca-gregory-nu-latn), and ar uses Latin digits
//                  (nu-latn) in dates, numbers and money for business
//                  consistency; fa keeps Persian digits.
//                  ky dates use ru-KG: CLDR's ky numeric date is "2026-11-10"
//                  (y-dd-MM), which reads like an ISO date with day and month
//                  swapped. Kyrgyz documents write 11.10.2026, and the ky
//                  month names are the Russian ones anyway.
// - `intlFallback` tag used when the browser has no CLDR data for the
//                  language. Chrome ships plural rules but no date / number
//                  data for az, uz, kk, ky and tk (it prints "2026 M10 11"
//                  and "-2 h"); Edge lacks az and kk; Node and Firefox have
//                  them all. Latin-script Turkic languages fall back to
//                  tr-TR, Cyrillic ones to Russian (co-official in KZ / KG).
// - `pluralLocale` locale for i18next's Intl.PluralRules when the base code
//                  is not the variant we ship: pt is European Portuguese,
//                  where 0 takes the plural form (pt / pt-BR: singular).
// `script` documents the writing system (see the font notes in index.css);
// `font` names a web font that is loaded only while the language is active.
// No flags: languages are not countries (ar, pt, fr, sw span many), and
// Windows has no flag emoji; the switcher shows the code instead.
export const LANGUAGES = [
  // Türkiye & the Turkic world
  { code: 'tr', label: 'Türkçe', short: 'TR', dir: 'ltr', group: 'turkic', script: 'Latn', intl: 'tr-TR' }, // i18n-ignore
  { code: 'az', label: 'Azərbaycan dili', short: 'AZ', dir: 'ltr', group: 'turkic', script: 'Latn', intl: 'az-AZ', numberIntl: 'az-Latn-AZ', dateIntl: 'az-Latn-AZ', intlFallback: 'tr-TR' },
  { code: 'uz', label: 'Oʻzbekcha', short: 'UZ', dir: 'ltr', group: 'turkic', script: 'Latn', intl: 'uz-UZ', numberIntl: 'uz-Latn-UZ', dateIntl: 'uz-Latn-UZ', intlFallback: 'tr-TR' },
  { code: 'kk', label: 'Қазақша', short: 'KK', dir: 'ltr', group: 'turkic', script: 'Cyrl', intl: 'kk-KZ', intlFallback: 'ru-KZ' },
  { code: 'ky', label: 'Кыргызча', short: 'KY', dir: 'ltr', group: 'turkic', script: 'Cyrl', intl: 'ky-KG', dateIntl: 'ru-KG', intlFallback: 'ru-KG' },
  { code: 'tk', label: 'Türkmençe', short: 'TK', dir: 'ltr', group: 'turkic', script: 'Latn', intl: 'tk-TM', intlFallback: 'tr-TR' }, // i18n-ignore
  // Europe
  { code: 'en', label: 'English', short: 'EN', dir: 'ltr', group: 'europe', script: 'Latn', intl: 'en-US' },
  { code: 'fr', label: 'Français', short: 'FR', dir: 'ltr', group: 'europe', script: 'Latn', intl: 'fr-FR' }, // i18n-ignore
  { code: 'pt', label: 'Português', short: 'PT', dir: 'ltr', group: 'europe', script: 'Latn', intl: 'pt-PT', pluralLocale: 'pt-PT' },
  { code: 'ru', label: 'Русский', short: 'RU', dir: 'ltr', group: 'europe', script: 'Cyrl', intl: 'ru-RU' },
  // Middle East & Africa
  { code: 'ar', label: 'العربية', short: 'AR', dir: 'rtl', group: 'middleEastAfrica', script: 'Arab', font: 'arabic', intl: 'ar-SA', numberIntl: 'ar-u-nu-latn', dateIntl: 'ar-u-ca-gregory-nu-latn' },
  { code: 'fa', label: 'فارسی', short: 'FA', dir: 'rtl', group: 'middleEastAfrica', script: 'Arab', font: 'arabic', intl: 'fa-IR', dateIntl: 'fa-IR-u-ca-gregory' },
  { code: 'sw', label: 'Kiswahili', short: 'SW', dir: 'ltr', group: 'middleEastAfrica', script: 'Latn', intl: 'sw-KE' },
  // Asia
  { code: 'ja', label: '日本語', short: 'JA', dir: 'ltr', group: 'asia', script: 'Jpan', intl: 'ja-JP' },
  { code: 'id', label: 'Bahasa Indonesia', short: 'ID', dir: 'ltr', group: 'asia', script: 'Latn', intl: 'id-ID' },
  { code: 'ms', label: 'Bahasa Melayu', short: 'MS', dir: 'ltr', group: 'asia', script: 'Latn', intl: 'ms-MY' },
  { code: 'vi', label: 'Tiếng Việt', short: 'VI', dir: 'ltr', group: 'asia', script: 'Latn', intl: 'vi-VN' },
].map((lang) => ({ ...lang, inProgress: IN_PROGRESS_LANGUAGES.includes(lang.code) }));

export const SUPPORTED_LANGUAGE_CODES = LANGUAGES.map((l) => l.code);

/**
 * Display currency the pricing and checkout pages preselect for a language
 * (only when the payment gateway supports it, otherwise TRY). Operator can
 * change this table without touching the backend.
 */
const LANGUAGE_CURRENCY = {
  tr: 'TRY', en: 'USD', ja: 'JPY', fa: 'USD', ru: 'RUB',
  fr: 'EUR', pt: 'EUR',
  az: 'USD', uz: 'USD', kk: 'USD', ky: 'USD', tk: 'USD',
  ar: 'USD', sw: 'USD', id: 'USD', ms: 'USD', vi: 'USD',
};

export function preferredCurrency(code) {
  return LANGUAGE_CURRENCY[normalizeLanguage(code)] || 'TRY';
}

/** Right-to-left languages (<html dir="rtl">). */
export const RTL_LANGUAGES = LANGUAGES.filter((l) => l.dir === 'rtl').map((l) => l.code);

/**
 * i18next `fallbackLng`: where a key missing in a language is looked up.
 * - Latin-script Turkic languages (az, uz, tk) -> tr, then en: their readers
 *   read Turkish far more easily than English, and tr is the reference.
 * - Cyrillic Turkic languages (kk, ky) -> ru, then en: Russian is co-official
 *   in Kazakhstan and Kyrgyzstan, and the script matches.
 * - Every other new language -> en.
 * - en, ja, fa, ru and unknown codes keep the original behaviour: tr.
 * i18next fetches only the active language and its chain.
 */
export const FALLBACK_LANGUAGES = {
  az: ['tr', 'en'],
  uz: ['tr', 'en'],
  tk: ['tr', 'en'],
  kk: ['ru', 'en'],
  ky: ['ru', 'en'],
  ar: ['en'],
  fr: ['en'],
  pt: ['en'],
  sw: ['en'],
  id: ['en'],
  ms: ['en'],
  vi: ['en'],
  default: [DEFAULT_LANGUAGE],
};

const BY_CODE = Object.fromEntries(LANGUAGES.map((l) => [l.code, l]));

// Legacy or individual-language codes some browsers and OSes still send.
const ALIASES = { in: 'id', zsm: 'ms', arb: 'ar', pes: 'fa', uzn: 'uz', azj: 'az' };

/**
 * Map any language tag to a supported base code: "en-US" / "en_GB" / "EN"
 * -> "en", "uz-Latn-UZ" -> "uz", "pt-BR" -> "pt" (we ship European
 * Portuguese; Brazilian readers get it rather than nothing). Returns null for
 * empty or unsupported tags (e.g. "de-DE", "zh"), so callers can decide on
 * the fallback.
 */
export function normalizeLanguage(code) {
  if (typeof code !== 'string') return null;
  let base = code.trim().toLowerCase().split(/[-_]/)[0];
  base = ALIASES[base] || base;
  return BY_CODE[base] ? base : null;
}

/** Language entry for a code (any variant); the default language if unknown. */
export function getLanguage(code) {
  return BY_CODE[normalizeLanguage(code)] || BY_CODE[DEFAULT_LANGUAGE];
}

/** The languages a key is looked up in for `code`, in order (code first). */
export function fallbackChain(code) {
  const lang = normalizeLanguage(code) || DEFAULT_LANGUAGE;
  const chain = [lang, ...(FALLBACK_LANGUAGES[lang] || FALLBACK_LANGUAGES.default)];
  return chain.filter((c, i) => chain.indexOf(c) === i);
}

// Search aid only (never displayed): "arabic" or "kazakh" finds the native label.
const ENGLISH_NAMES = {
  tr: 'Turkish', az: 'Azerbaijani', uz: 'Uzbek', kk: 'Kazakh', ky: 'Kyrgyz', tk: 'Turkmen',
  en: 'English', fr: 'French', pt: 'Portuguese', ru: 'Russian',
  ar: 'Arabic', fa: 'Persian Farsi', sw: 'Swahili',
  ja: 'Japanese', id: 'Indonesian', ms: 'Malay', vi: 'Vietnamese',
};

const fold = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Languages grouped for the switcher, groups in LANGUAGE_GROUPS order:
 * [{ id, labelKey, languages: [...] }]. `query` filters by native label,
 * code or English name (case and accent insensitive, so "turkmence" finds
 * Türkmençe); groups without a match are left out.
 */
export function groupedLanguages(query = '') {
  const q = fold(query).trim();
  const match = (l) => !q
    || fold(l.label).includes(q)
    || l.code.startsWith(q)
    || fold(ENGLISH_NAMES[l.code]).includes(q);
  return LANGUAGE_GROUPS
    .map((g) => ({ ...g, languages: LANGUAGES.filter((l) => l.group === g.id && match(l)) }))
    .filter((g) => g.languages.length);
}

/** 'rtl' for right-to-left languages (ar, fa), otherwise 'ltr'. */
export function languageDir(code) {
  return getLanguage(code).dir;
}

/**
 * The active UI language as a supported base code: i18n.language (what was
 * chosen or detected, possibly a regional variant such as "en-US")
 * normalised, else i18n.resolvedLanguage, else tr.
 *
 * i18n.language comes first because i18next's resolvedLanguage skips a
 * language whose bundle is still empty: with an untranslated ar.json it
 * reports "en" (the fallback), which would give an Arabic reader
 * <html dir="ltr"> and an "EN" switcher. The chosen language decides
 * direction, Intl formats and the switcher; missing texts come from the
 * fallback chain.
 */
export function activeLanguage(i18n) {
  return (
    normalizeLanguage(i18n && i18n.language)
    || normalizeLanguage(i18n && i18n.resolvedLanguage)
    || DEFAULT_LANGUAGE
  );
}

// Web fonts loaded on demand (Google Fonts CSS; the woff2 files are split by
// unicode-range, so the browser only downloads the subsets a page uses).
// Inter (index.html) already covers Latin, Latin Extended (az ə, tk ň ž ý,
// uz oʻ gʻ), Vietnamese and Cyrillic incl. Cyrillic Extended (kk/ky ә ғ қ ң
// ө ұ ү һ і); Arabic script is the only one it lacks.
export const LANGUAGE_FONTS = {
  arabic: 'https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;500;600;700&display=swap',
};

/** Add the web font stylesheet the language needs, once. */
export function ensureLanguageFont(code, doc = typeof document !== 'undefined' ? document : null) {
  const font = getLanguage(code).font;
  if (!font || !LANGUAGE_FONTS[font] || !doc || !doc.head) return;
  const id = `runvya-font-${font}`;
  if (doc.getElementById(id)) return;
  const link = doc.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = LANGUAGE_FONTS[font];
  doc.head.appendChild(link);
}

/** Set <html lang> and <html dir> for the given language (and its font). */
export function applyDocumentLanguage(code, doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || !doc.documentElement) return;
  const lang = getLanguage(code);
  doc.documentElement.setAttribute('lang', lang.code);
  doc.documentElement.setAttribute('dir', lang.dir);
  ensureLanguageFont(lang.code, doc);
}

function safeStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (_) {
    return null;
  }
}

/** Persist the chosen language. Never throws (private mode, blocked storage). */
export function storeLanguage(code, storage = safeStorage()) {
  const lang = normalizeLanguage(code);
  if (!lang || !storage) return;
  try { storage.setItem(LANGUAGE_STORAGE_KEY, lang); } catch (_) { /* ignore */ }
}

/**
 * Fix a stored choice from before normalisation existed: "en-US" -> "en".
 * An unsupported value (e.g. "de") is removed so browser detection runs
 * again instead of pinning the fallback forever. Returns the stored code.
 */
export function normalizeStoredLanguage(storage = safeStorage()) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(LANGUAGE_STORAGE_KEY);
    if (raw === null) return null;
    const lang = normalizeLanguage(raw);
    if (!lang) {
      storage.removeItem(LANGUAGE_STORAGE_KEY);
      return null;
    }
    if (lang !== raw) storage.setItem(LANGUAGE_STORAGE_KEY, lang);
    return lang;
  } catch (_) {
    return null;
  }
}

/**
 * Switch the UI language from a switcher. Normalises the code, persists it
 * and returns i18n.changeLanguage's promise. <html lang/dir> follow through
 * the languageChanged listener registered in src/i18n.js.
 */
export function switchLanguage(i18n, code) {
  const lang = normalizeLanguage(code) || DEFAULT_LANGUAGE;
  storeLanguage(lang);
  return i18n.changeLanguage(lang);
}
